import { ISubCategoryCharacteristic, ICharacteristic, IFrontVariation } from 'models/product'
import { productApi } from 'api/product'




/**
 * Получает характеристики подкатегории с сервера
 * @param subCategoryId - ID подкатегории
 * @returns массив характеристик подкатегории
 */
export const fetchSubCategoryCharacteristics = async (
  subCategoryId: string
): Promise<ISubCategoryCharacteristic[]> => {
  try {
    const response = await productApi.getSubCategoryCharacteristics(subCategoryId)
    return response.data.characteristics || []
  } catch {
    return []
  }
}

/**
 * Извлекает заблокированные характеристики для вариации из основного товара
 * @param variation - вариация товара
 * @returns массив заблокированных характеристик для вариации (максимум 2)
 */
export const extractVariationCharacteristics = (
  variation: IFrontVariation
): ICharacteristic[] => {
  const variationCharacteristics: ICharacteristic[] = []

  // Добавляем первую характеристику с заблокированным значением из вариации
  if (variation.firstCharacteristicName) {
    variationCharacteristics.push({
      name: variation.firstCharacteristicName,
      value: variation.firstCharacteristicValue || ''
    })
  }

  // Добавляем вторую характеристику с заблокированным значением из вариации
  if (variation.secondCharacteristicName) {
    variationCharacteristics.push({
      name: variation.secondCharacteristicName,
      value: variation.secondCharacteristicValue || ''
    })
  }

  return variationCharacteristics
}

const normalizeName = (name?: string): string => (name ?? '').trim().toLowerCase()

const findByName = (
  characteristics: ICharacteristic[],
  name?: string
): ICharacteristic | undefined =>
  characteristics.find((item) => normalizeName(item.name) === normalizeName(name))

/**
 * Собирает полный список характеристик товара-вариации: характеристики подкатегории,
 * затем всё остальное, что уже было у товара. Значения из строки таблицы вариаций
 * перетирают одноимённые характеристики товара — иначе привязка вариации оставляла бы
 * от товара только размер/цвет
 * @param productCharacteristics - собственные характеристики товара (или родителя при создании)
 * @param variationCharacteristics - характеристики из строки таблицы вариаций
 * @param subCategoryCharacteristics - характеристики подкатегории основного товара
 */
export const buildVariationCharacteristics = (
  productCharacteristics: ICharacteristic[],
  variationCharacteristics: ICharacteristic[],
  subCategoryCharacteristics: ISubCategoryCharacteristic[]
): ICharacteristic[] => {
  const getValue = (name: string): string => {
    // пустое значение в таблице означает, что характеристика для этой строки не задана
    const fromVariation = findByName(variationCharacteristics, name)?.value?.trim()
    return fromVariation || findByName(productCharacteristics, name)?.value || ''
  }

  const result: ICharacteristic[] = subCategoryCharacteristics.map((item) => ({
    name: item.name,
    value: getValue(item.name)
  }))

  ;[...variationCharacteristics, ...productCharacteristics].forEach((item) => {
    if (!item.name?.trim() || findByName(result, item.name)) return
    result.push({ name: item.name, value: getValue(item.name) })
  })

  return result
}

export interface ICharacteristicConflict {
  name: string
  productValue: string
  variationValue: string
}

/**
 * Ищет характеристики, у которых значение товара расходится со значением из таблицы вариаций
 * @param productCharacteristics - собственные характеристики товара
 * @param variationCharacteristics - характеристики из строки таблицы вариаций
 * @returns список расхождений (пустые значения товара расхождением не считаются)
 */
export const getVariationCharacteristicConflicts = (
  productCharacteristics: ICharacteristic[],
  variationCharacteristics: ICharacteristic[]
): ICharacteristicConflict[] =>
  variationCharacteristics.reduce<ICharacteristicConflict[]>((conflicts, variationCharacteristic) => {
    const variationValue = variationCharacteristic.value?.trim() ?? ''
    const productValue =
      findByName(productCharacteristics, variationCharacteristic.name)?.value?.trim() ?? ''

    if (
      variationValue &&
      productValue &&
      productValue.toLowerCase() !== variationValue.toLowerCase()
    ) {
      conflicts.push({
        name: variationCharacteristic.name,
        productValue,
        variationValue
      })
    }

    return conflicts
  }, [])
